import { Injectable } from "@angular/core";
import { ExcelAddInDto, ExcelTable } from "@ankaadia/graphql";
import { DateTime } from "@ankaadia/ankaadia-shared";

export interface IresultInfo {
  finishState: string;
  issues: string;
  timeTaken: string;
}

@Injectable({ providedIn: "root" })
export class WriteTablesService {
  async writeDataToManyTables(tableData: ExcelAddInDto, addIndex = true): Promise<IresultInfo> {
    let ctr = 0;
    const timestamp = DateTime.now().getTime();

    const results = await Promise.all(
      tableData.dataTables.map(async (table) => await this.writeDataToTable(table, addIndex ? ctr++ : null))
    );
    const issues = results.filter((result) => result != "success").join(", ");

    return {
      finishState: issues ? "warn" : "success",
      issues: issues,
      timeTaken: ((DateTime.now().getTime() - timestamp) / 1000).toString(),
    };
  }

  async writeDataToTable(tableData: ExcelTable, index: number): Promise<string> {
    try {
      await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        const app = context.workbook.application;

        let tableName = tableData.label.substring(0, 27);
        if (index) tableName = tableName + "_" + index;
        let currentWorksheet: Excel.Worksheet;

        sheets.load("items/name");
        await context.sync();

        if (!sheets.items.some((e) => e.name === tableName)) {
          currentWorksheet = sheets.add(tableName);
          await context.sync();
          // sheet.load("name, position");
        } else {
          currentWorksheet = context.workbook.worksheets.getItem(tableName);
        }

        currentWorksheet.load("name, position");
        await context.sync();

        currentWorksheet.getRange().clear();
        await context.sync();

        // Build the full grid (header row + data rows) and write it to the worksheet range
        // BEFORE creating the table, then create the table over the already-filled range.
        // Why not rows.add(): it forces full-width rows, so a single request can exceed the
        // payload-size limit on very wide tables (e.g. 2 rows x 3324 columns). Writing to the
        // range lets us split the grid into blocks bounded by cell count, keeping every request
        // small regardless of width or height. Filling the cells first also stops tables.add()
        // from generating Column1..N names that would otherwise race with our writes on wide tables.
        const grid = [tableData.columns.map((item) => item.label), ...(tableData.dataCells ?? [])];
        const totalRows = grid.length;
        const totalColumns = tableData.columns.length;

        // Bound each request by cell count, chunking across BOTH columns and rows so neither a
        // very wide nor a very tall table can exceed the payload limit.
        const CELLS_PER_REQUEST = 5000;
        const MAX_COLUMNS_PER_CHUNK = 500;
        const columnsPerChunk = Math.min(MAX_COLUMNS_PER_CHUNK, totalColumns);
        const rowsPerChunk = Math.max(1, Math.floor(CELLS_PER_REQUEST / columnsPerChunk));
        for (let startCol = 0; startCol < totalColumns; startCol += columnsPerChunk) {
          const endCol = Math.min(startCol + columnsPerChunk, totalColumns);
          const startColumn = this.number2Digit(startCol + 1);
          const endColumn = this.number2Digit(endCol);
          for (let startRow = 0; startRow < totalRows; startRow += rowsPerChunk) {
            const endRow = Math.min(startRow + rowsPerChunk, totalRows);
            const values = grid.slice(startRow, endRow).map((row) => row.slice(startCol, endCol));
            // These only stay in effect until the next context.sync(), so re-apply every iteration.
            app.suspendApiCalculationUntilNextSync();
            app.suspendScreenUpdatingUntilNextSync();
            // grid row 0 is worksheet row 1, so offset the row addresses by 1.
            currentWorksheet.getRange(`${startColumn}${startRow + 1}:${endColumn}${endRow}`).values = values;
            await context.sync();
          }
        }

        const candidateTable = currentWorksheet.tables.add(
          `A1:${this.number2Digit(totalColumns)}${totalRows}`,
          true /*hasHeaders*/
        );
        await context.sync();

        candidateTable.name = index
          ? tableName.replace(/[\s-]/g, "").substring(0, 30) + index
          : tableName.replace(/[\s-]/g, "").substring(0, 32);
        await context.sync();

        currentWorksheet.getUsedRange().format.autofitColumns();
        await context.sync();

        currentWorksheet.calculate(true); // We calculate and activate the sheet in order to make sure that they appear in the workbook
        await context.sync();

        currentWorksheet.activate();
        await context.sync();
      });
      return "success";
    } catch (e) {
      // Return a non-empty identifier so the failure is always counted as an issue by the
      // caller (an empty/undefined string would be dropped by join() and masked as success).
      return tableData?.label || "unknown table";
    }
  }

  private number2Digit(num: number): string {
    if (num <= 0) return undefined;
    if (num <= 26) {
      return (num + 9).toString(36).toUpperCase();
    } else {
      if (num % 26 === 0) {
        return this.number2Digit(Math.floor(num / 26 - 1)) + "Z";
      } else {
        return this.number2Digit(Math.floor(num / 26)) + ((num % 26) + 9).toString(36).toUpperCase();
      }
    }
  }
}
